/**
 * ScheduleTimeline — pure display component for generated schedules.
 *
 * Renders a scrollable list of containers with computed start times,
 * day headers (for multi-day retreats), sleep styling, warning
 * indicators, and an "Ends ..." footer.
 *
 * UI-INV-005: Leaf component, props only, no engine imports.
 */
import type { EditableRetreatSchedule } from "@retreat/core";
import type { ScheduleWarning } from "@retreats/content";
import { TEST_IDS } from "../testing/testIds";
import styles from "./ScheduleTimeline.module.css";

// ── helpers ──────────────────────────────────────────────────────────────────

function fmtTime(ms: number): string {
  const d = new Date(ms);
  const h = d.getHours();
  const m = d.getMinutes();
  const period = h < 12 ? "AM" : "PM";
  const display = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${display}:${m.toString().padStart(2, "0")} ${period}`;
}

function fmtDuration(secs: number): string {
  const h = Math.floor(secs / 3600);
  const m = Math.round((secs % 3600) / 60);
  if (h > 0 && m > 0) return `${h}h ${m}m`;
  if (h > 0) return `${h}h`;
  return `${m}m`;
}

const DAY_NAMES = [
  "SUNDAY", "MONDAY", "TUESDAY", "WEDNESDAY",
  "THURSDAY", "FRIDAY", "SATURDAY",
];

// ── types ─────────────────────────────────────────────────────────────────────

type Props = {
  schedule: EditableRetreatSchedule;
  startDateTime: Date;
  warnings: ScheduleWarning[];
  onContainerLabelChange?: (containerId: string, label: string) => void;
};

// ── component ─────────────────────────────────────────────────────────────────

export function ScheduleTimeline({
  schedule,
  startDateTime,
  warnings: _warnings,
  onContainerLabelChange: _onContainerLabelChange,
}: Props) {
  const isMultiDay = (schedule.days?.length ?? 0) > 1;

  // Walk containers and accumulate wall-clock timestamps
  let cursorMs = startDateTime.getTime();

  type Row = {
    containerId: string;
    name: string;
    isSleep: boolean;
    isWarned: boolean;
    startMs: number;
    durationSeconds: number;
  };
  type DayBlock = {
    dayIdx: number;
    dayStartMs: number;
    rows: Row[];
  };

  const dayBlocks: DayBlock[] = (schedule.days ?? []).map((day, dayIdx) => {
    const dayStartMs = cursorMs;
    const rows: Row[] = (day.containers ?? []).map((c) => {
      const startMs = cursorMs;
      cursorMs += c.durationSeconds * 1000;
      return {
        containerId: c.id,
        name: c.name,
        isSleep: c.type === "sleep",
        isWarned: false,
        startMs,
        durationSeconds: c.durationSeconds,
      };
    });
    return { dayIdx, dayStartMs, rows };
  });

  const endMs = cursorMs;

  // For single-day retreats that cross midnight, inject a "day boundary" header
  // between the last container of one day and the first container of the next.
  const startDate = startDateTime.toDateString();

  return (
    <div className={styles.timeline} data-testid={TEST_IDS.SCHEDULE_TIMELINE}>
      {dayBlocks.map(({ dayIdx, dayStartMs, rows }) => (
        <div key={dayIdx} className={styles.dayBlock}>
          {/* Multi-day retreats: show day header from template day structure */}
          {isMultiDay && (
            <div className={styles.dayHeader}>
              {DAY_NAMES[new Date(dayStartMs).getDay()]}
            </div>
          )}
          {rows.map((row, rowIdx) => {
            const rowDate = new Date(row.startMs).toDateString();
            const prevMs = rowIdx > 0 ? rows[rowIdx - 1].startMs : null;
            const prevDate = prevMs ? new Date(prevMs).toDateString() : startDate;
            // Inject midnight-crossing header for single-day retreats
            const crossesMidnight = !isMultiDay && rowDate !== prevDate;

            return (
              <div key={row.containerId}>
                {crossesMidnight && (
                  <div className={styles.dayHeader}>
                    {DAY_NAMES[new Date(row.startMs).getDay()]}
                  </div>
                )}
                <div
                  className={[styles.row, row.isSleep ? styles.rowSleep : ""]
                    .filter(Boolean)
                    .join(" ")}
                >
                  <span className={styles.rowIcon}>
                    {row.isSleep ? "💤" : ""}
                  </span>
                  <span className={styles.rowTime}>{fmtTime(row.startMs)}</span>
                  <span className={styles.rowName}>{row.name}</span>
                  <span className={styles.rowDuration}>
                    {fmtDuration(row.durationSeconds)}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      ))}

      {/* Footer */}
      <div className={styles.footer}>
        Ends{" "}
        {new Date(endMs).toDateString() !== startDate
          ? `${DAY_NAMES[new Date(endMs).getDay()]} `
          : ""}
        {fmtTime(endMs)}
      </div>
    </div>
  );
}
